import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Button, Dialog } from 'vnetwork-ui'
import { useEInvoiceErrorText } from '../../hooks/use-e-invoice'
import { saveEInvoiceItems } from '../../services'
import { EInvoiceItems } from '../../types'
import EInvoiceItemsEditor from './EInvoiceItemsEditor'

interface EditEInvoiceItemsDialogProps {
    open: boolean
    onClose: () => void
    invoiceID: number
    lines: EInvoiceItems
}

/**
 * Sửa nội dung dòng hàng của hoá đơn điện tử trước khi phát hành (PUT .../e-invoice/items).
 * Lưu xong, khối "Hoá đơn điện tử" và popup phát hành hiện nội dung đã sửa.
 */
const EditEInvoiceItemsDialog = ({
    open,
    onClose,
    invoiceID,
    lines,
}: EditEInvoiceItemsDialogProps) => {
    const { t } = useTranslation('invoice')
    const errorText = useEInvoiceErrorText()
    const [values, setValues] = useState<Record<number, string>>(() =>
        Object.fromEntries(
            lines.items.map((item) => [item.invoice_item_id, item.description])
        )
    )

    const { mutateAsync: save, isPending } = useMutationWithHandler(
        { mutationFn: saveEInvoiceItems },
        {
            // Cùng tiền tố key với danh sách dòng hàng của khối "Hoá đơn điện tử" → tự tải lại
            reactQueryKeys: QUERY_KEYS.INVOICE_E_INVOICE,
            showSuccessToast: false,
            onSuccess: () => {
                toast.success(t('e-invoice-items-saved'))
                onClose()
            },
            showErrorToast: false,
            onError: (error) => toast.error(errorText(error)),
        }
    )

    const itemDescriptions = lines.items.map((item) => ({
        invoice_item_id: item.invoice_item_id,
        description: (values[item.invoice_item_id] ?? '').trim(),
    }))
    const hasEmpty = itemDescriptions.some((item) => !item.description)
    const changed = lines.items.some(
        (item, index) =>
            itemDescriptions[index].description !== item.description
    )

    return (
        <Dialog
            open={open}
            onClose={() => !isPending && onClose()}
            maxWidth="lg"
            title={t('e-invoice-items-edit-title')}
            actions={
                <div className="flex w-full justify-end gap-2">
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={isPending}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={() =>
                            save({
                                invoiceID,
                                item_descriptions: itemDescriptions,
                            })
                        }
                        disabled={hasEmpty || !changed || isPending}
                        loading={isPending}
                    >
                        {t('e-invoice-items-save')}
                    </Button>
                </div>
            }
        >
            <div className="space-y-2 text-sm">
                <EInvoiceItemsEditor
                    items={lines.items}
                    values={values}
                    onChange={setValues}
                    currency={lines.currency}
                    disabled={isPending}
                />
                <span className="block text-xs text-gray-500">
                    {t('e-invoice-items-edit-hint')}
                </span>
                {hasEmpty && (
                    <span className="block text-red-600">
                        {t('e-invoice-item-description-required')}
                    </span>
                )}
            </div>
        </Dialog>
    )
}

export default EditEInvoiceItemsDialog
